import { DriveItem } from '@microsoft/microsoft-graph-types'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import { FolderUsageCheck } from './FolderPathField'

export enum FolderUsageType {
    File = 'FILE',
    Party = 'PARTY',
}

export type FolderUsageDto = {
    type: FolderUsageType
    /** Dossier number. Absent for a CRM relation, which has no reference. */
    reference?: string
    name: string
}

type Options = {
    /** The dossier being edited, so it is not reported as occupying its own folder. */
    excludeFile?: string
    /** The party being edited, so it is not reported as occupying its own folder. */
    excludeParty?: string
}

/**
 * Asks the backend whether a folder is already claimed by a dossier or a CRM relation, and turns
 * the answer into the phrase the field puts in its message. One lookup covers both, because a
 * folder claimed by either one is equally off limits to the other.
 */
export function useFolderUsageCheck(options: Options = {}): FolderUsageCheck {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    return (item: DriveItem, signal: AbortSignal) =>
        axios
            .get<FolderUsageDto>('/graph/document-folder-check', {
                params: { location: item.id, excludeFile: options.excludeFile, excludeParty: options.excludeParty },
                headers: { SuppressErrors: 'true' },
                signal,
            })
            .then(response => {
                if (!response.data) {
                    return undefined
                }
                return response.data.type === FolderUsageType.File
                    ? translate('base.folderpath.usage.file', { reference: response.data.reference, name: response.data.name })
                    : translate('base.folderpath.usage.party', { name: response.data.name })
            })
            .catch(() => {
                // A failed lookup must not read as "free". Reporting it as occupied would be a lie
                // too, so let the field carry on: the create still fails loudly on conflict.
                return undefined
            })
}
